import type { TrackerData } from "@finn-no/pulse-sdk";
import { type RefObject, useEffect, useRef } from "react";
import usePulse from "./usePulse.ts";

/**
 * Fires a Pulse `View` event the first time the referenced element becomes visible, at most once
 * per page load. `usePulse` returns fresh functions every render, so it is read through a ref to
 * keep the observer from being rebuilt — and re-fired — on each one.
 *
 * @param threshold How much of the element has to be visible. Sections taller than the viewport
 *   can never reach a fraction, so those want 0.
 */
export const useTrackViewOnce = <T extends Element>(
  ref: RefObject<T | null>,
  data: TrackerData,
  threshold = 0.5,
): void => {
  const { trackElementView } = usePulse();
  const latest = useRef({ trackElementView, data });
  const hasFired = useRef(false);

  useEffect(() => {
    latest.current = { trackElementView, data };
  });

  useEffect(() => {
    const element = ref.current;
    // Absent on the server and in happy-dom, where there is nothing to be visible in anyway
    if (!element || typeof IntersectionObserver === "undefined") {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (hasFired.current || !entries.some((entry) => entry.isIntersecting)) {
          return;
        }
        hasFired.current = true;
        latest.current.trackElementView(latest.current.data);
        observer.disconnect();
      },
      { threshold },
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [ref, threshold]);
};
